// Serviço para geocodificação de endereços
import { GOOGLE_MAPS_CONFIG } from '../config/maps';

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
  place_id: string;
}

export interface GeocodingResponse {
  results: Array<{
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
    formatted_address: string;
    place_id: string;
  }>;
  status: string;
}

class GeocodingService {
  private cache = new Map<string, GeocodingResult>();

  /**
   * Converte um endereço em coordenadas usando Google Maps Geocoding API
   */
  async geocodeAddress(address: string): Promise<GeocodingResult | null> {
    // Verificar cache primeiro
    if (this.cache.has(address)) {
      return this.cache.get(address)!;
    }

    try {
      if (!GOOGLE_MAPS_CONFIG.apiKey) {
        console.warn('API Key do Google Maps não configurada para geocodificação');
        return null;
      }

      const encodedAddress = encodeURIComponent(address);
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${GOOGLE_MAPS_CONFIG.apiKey}&region=${GOOGLE_MAPS_CONFIG.region}&language=${GOOGLE_MAPS_CONFIG.language}`;

      const response = await fetch(url);
      const data: GeocodingResponse = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const result = data.results[0];
        const geocodingResult: GeocodingResult = {
          latitude: result.geometry.location.lat,
          longitude: result.geometry.location.lng,
          formatted_address: result.formatted_address,
          place_id: result.place_id
        };

        this.cache.set(address, geocodingResult);
        return geocodingResult;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Converte um endereço completo em coordenadas
   */
  async geocodeFullAddress(endereco: string, cidade: string, estado: string, cep?: string): Promise<GeocodingResult | null> {
    const fullAddress = `${endereco}, ${cidade}, ${estado}${cep ? `, ${cep}` : ''}, Brasil`;
    return this.geocodeAddress(fullAddress);
  }

  /**
   * Converte múltiplos endereços em coordenadas
   */
  async geocodeMultipleAddresses(addresses: string[]): Promise<Map<string, GeocodingResult>> {
    const results = new Map<string, GeocodingResult>();
    
    // Processar em lotes para evitar rate limiting
    const batchSize = 5;
    for (let i = 0; i < addresses.length; i += batchSize) {
      const batch = addresses.slice(i, i + batchSize);
      const promises = batch.map(async (address) => {
        const result = await this.geocodeAddress(address);
        if (result) {
          results.set(address, result);
        }
      });
      
      await Promise.all(promises);
      
      // Pequena pausa entre lotes
      if (i + batchSize < addresses.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    return results;
  }

  /**
   * Limpa o cache de geocodificação
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Retorna o tamanho do cache
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

export const geocodingService = new GeocodingService();
